import { toast } from 'sonner';

/**
 * Copies a URL to the clipboard and shows a success toast notification.
 * This matches the behavior of the opportunity assessment share functionality.
 */
export const copyToClipboardAndToast = async (url: string, context: string = 'Link'): Promise<void> => {
  try {
    await navigator.clipboard.writeText(url);
    
    toast.success(`${context} Copied Successfully!`, {
      description: 'Link copied to clipboard! You can now paste it anywhere to share.',
      duration: 8000,
    });
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    toast.error('Failed to copy link to clipboard. Please try again.');
  }
};

/**
 * Creates a share URL with a hash fragment for auto-scrolling to a specific chart.
 * @param baseUrl - The base URL to share
 * @param chartTitle - The title of the chart to scroll to
 * @param tabId - The tab ID to differentiate between tabs (optional)
 * @returns URL with hash fragment
 */
export const createChartShareUrl = (baseUrl: string, chartTitle: string, tabId?: string): string => {
  // Remove any existing hash from the base URL to prevent double hashes
  const urlWithoutHash = baseUrl.split('#')[0];
  
  // Convert chart title to a URL-friendly hash
  const hash = chartTitle
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
  
  // Add tab prefix if provided
  const tabPrefix = tabId ? `${tabId}-` : '';
  
  const finalUrl = `${urlWithoutHash}#${tabPrefix}${hash}`;
  
  console.log(`🔗 Creating share URL:`, {
    originalUrl: baseUrl,
    urlWithoutHash,
    chartTitle,
    tabId,
    hash,
    tabPrefix,
    finalUrl
  });
  
  return finalUrl;
};

/**
 * Creates a chart ID with optional tab prefix to avoid conflicts across tabs.
 * @param chartTitle - The title of the chart
 * @param tabId - The tab ID to differentiate between tabs (optional)
 * @returns Chart ID with tab prefix
 */
export const createChartId = (chartTitle: string, tabId?: string): string => {
  // Convert chart title to a URL-friendly ID
  const id = chartTitle
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    .trim();
  
  // Add tab prefix if provided
  const tabPrefix = tabId ? `${tabId}-` : '';
  
  return `${tabPrefix}${id}`;
};

/**
 * Scrolls to an element with a specific ID, with retry logic for dynamically loaded content.
 * @param hash - The hash fragment from the URL (without the #)
 * @param maxRetries - Maximum number of retry attempts (default: 10)
 * @param retryDelay - Delay between retries in milliseconds (default: 500)
 */
export const scrollToHashElement = async (
  hash: string, 
  maxRetries: number = 10, 
  retryDelay: number = 500
): Promise<void> => {
  if (!hash) return;

  const scrollToElement = (): boolean => {
    const element = document.getElementById(hash);
    if (element) {
      console.log(`🎯 Found element with id "${hash}", scrolling to it`);
      element.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start',
        inline: 'nearest'
      });
      return true;
    }
    
    // Debug: List all available IDs to help troubleshoot
    console.log(`🔍 Element with id "${hash}" not found. Available chart IDs:`, 
      Array.from(document.querySelectorAll('[id^="pc-"], [id^="sn-"], [id^="ot-"]'))
        .map(el => el.id)
        .sort()
    );
    
    return false;
  };

  // Try immediately first
  if (scrollToElement()) return;

  // If not found, retry with delays (for dynamically loaded content)
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    await new Promise(resolve => setTimeout(resolve, retryDelay));
    
    if (scrollToElement()) {
      console.log(`✅ Scrolled to element #${hash} after ${attempt} attempts`);
      return;
    }
    
    console.log(`⏳ Attempt ${attempt}/${maxRetries}: Element #${hash} not found yet, retrying in ${retryDelay}ms...`);
  }

  console.warn(`❌ Element with id "${hash}" not found after ${maxRetries} attempts`);
};

/**
 * Handles hash-based scrolling on page load, designed for dynamically loaded content.
 * Call this function after the page content has loaded.
 */
export const handleHashScroll = (): void => {
  const hash = window.location.hash.slice(1); // Remove the # symbol
  if (hash) {
    console.log(`🔗 Hash scroll requested for: #${hash}`);
    console.log(`🔍 Looking for element with id: "${hash}"`);
    // Small delay to ensure the page is fully rendered
    setTimeout(() => {
      scrollToHashElement(hash);
    }, 100);
  }
};
